import { useStore } from '@mirai/data-sources';
import { useLocale } from '@mirai/locale';
import { Notification } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';

import { ERROR, SCRIPT_DATA_ID, SCRIPT_DATA_NAME, SCRIPT_DOMAIN, SCRIPT_ID, SCRIPT_NAME } from './Openpay.constants';
import { L10N } from './Openpay.l10n';
import { trackError } from '../../../../../helpers/trackError';
import { EVENT } from '../../../ButtonPayment/ButtonPayment.constants';
import { Card } from '../Card/Card';

const Openpay = ({
  domainCountry = 'mx',
  merchantId,
  mode,
  publicKey,
  onChange = () => {},
  onError = () => {},
  ...others
}) => {
  const { translate } = useLocale();
  const { value: { payment = {} } = {}, value, set } = useStore();

  const [form, setForm] = useState({});
  const [formError, setFormError] = useState({});
  const [ready, setReady] = useState(false);
  const [dataReady, setDataReady] = useState(false);
  const [responseError, setResponseError] = useState();

  const refDevice = useRef();

  useEffect(() => {
    const exists = document.getElementById(SCRIPT_ID);
    if (!exists) {
      const script = document.createElement('script');
      script.id = SCRIPT_ID;
      script.src = `${SCRIPT_DOMAIN[domainCountry]}${SCRIPT_NAME}`;
      script.onload = handleLoad;

      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(SCRIPT_ID);
      el.parentNode.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready) return;

    const exists = document.getElementById(SCRIPT_DATA_ID);
    if (!exists) {
      const script = document.createElement('script');
      script.id = SCRIPT_DATA_ID;
      script.src = `${SCRIPT_DOMAIN[domainCountry]}${SCRIPT_DATA_NAME}`;
      script.onload = handleDataLoad;

      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(SCRIPT_DATA_ID);
      el.parentNode.removeChild(el);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  useEffect(() => {
    document.addEventListener(EVENT.CTA_PRESS, handleButtonPress);

    return () => document.removeEventListener(EVENT.CTA_PRESS, handleButtonPress);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, form, formError, ready, responseError, value]);

  useEffect(() => {
    if (!dataReady || !ready || !merchantId || !publicKey) return;

    const { OpenPay } = window;
    OpenPay.setId(merchantId);
    OpenPay.setApiKey(publicKey);
    OpenPay.setSandboxMode(mode === 'TEST');
    refDevice.current = OpenPay.deviceData.setup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataReady, merchantId, publicKey, ready]);

  const handleButtonPress = () => {
    if (Object.entries(formError).filter(([, value]) => value !== undefined).length > 0) return;

    setResponseError();
    const [month, year] = form.expire.split('/');
    window.OpenPay.token.create(
      {
        card_number: form.card?.trim()?.replace(/\s/g, ''),
        holder_name: form.name,
        expiration_year: year,
        expiration_month: month,
        cvv2: form.CVV,
      },
      handleResponse,
      handleResponseError,
    );
  };

  const handleChange = (values) => {
    setForm(values);

    const { type } = values;
    onChange({ type });
  };

  const handleError = (error) => {
    setFormError(error);
    onError(error);
  };

  const handleLoad = () => setReady(true);

  const handleDataLoad = () => setDataReady(true);

  const handleResponse = ({ data: { id: token } = {} } = {}) => {
    set({ payment: { deviceSessionId: refDevice.current, method: payment.method, token } });
  };

  const handleResponseError = (error) => {
    if (error) trackError('Openpay', error instanceof Error ? error.stack : error?.message);

    setResponseError(ERROR);
    set({ payment: { ...payment, error: ERROR } });
    // eslint-disable-next-line no-console
    console.error(error);
  };

  return (
    <>
      <Card {...others} cards={undefined} showCVV onChange={handleChange} onError={handleError} />
      {responseError && (
        <Notification error small wide>
          {translate(L10N.NOTIFICATION_ERROR_OPENPAY)}
        </Notification>
      )}
    </>
  );
};

Openpay.displayName = 'Mirai:Payments:Openpay';

Openpay.propTypes = {
  domainCountry: PropTypes.string,
  merchantId: PropTypes.string,
  mode: PropTypes.string,
  publicKey: PropTypes.string,
  onChange: PropTypes.func,
  onError: PropTypes.func,
};

export { Openpay };
